import { useMemo, useEffect, useState, useRef } from 'react';
import { isScheduleActiveNow, getNextScheduleChangeTime } from 'shared/helpers/schedule';
import { GqlSchedule } from 'types/graphql';

type UseActiveScheduledItemsOptions<T> = {
  items: T[];
  getSchedule: (item: T) => GqlSchedule | null | undefined;
  dispensaryTimezone?: string | null;
  enablePolling?: boolean;
  /** @deprecated No longer used. Schedules now update at exact change times. */
  pollingIntervalMs?: number;
};

const MAX_TIMEOUT_MS = 2147483647; // Maximum safe setTimeout value (~24.8 days)
const FALLBACK_CHECK_MS = 3600000; // 1 hour fallback if no schedule change detected

export function useActiveScheduledItems<T>({
  items,
  getSchedule,
  dispensaryTimezone = 'UTC',
  enablePolling = false,
}: UseActiveScheduledItemsOptions<T>): T[] {
  const timezone = dispensaryTimezone ?? 'UTC';
  const [tick, setTick] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enablePolling) {
      return undefined;
    }

    const scheduleNextCheck = (): void => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Only schedule updates when page is visible
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }

      // Get all schedules from items
      const schedules = items.map((item) => (item ? getSchedule(item) : null));

      // Calculate when the next schedule change will occur (returns timestamp in ms since epoch)
      const nextChangeTimestamp = getNextScheduleChangeTime(schedules, timezone);

      if (nextChangeTimestamp === null) {
        // No upcoming changes, use fallback interval
        timeoutRef.current = setTimeout(() => {
          setTick((prev) => prev + 1);
          scheduleNextCheck();
        }, FALLBACK_CHECK_MS);
        return;
      }

      // Calculate milliseconds until next change
      const now = Date.now();
      const msUntilChange = Math.max(0, nextChangeTimestamp - now);

      // Cap at maximum safe timeout value
      const safeTimeout = Math.min(msUntilChange, MAX_TIMEOUT_MS);

      timeoutRef.current = setTimeout(() => {
        setTick((prev) => prev + 1);
        scheduleNextCheck();
      }, safeTimeout);
    };

    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        // Page hidden: clear timeout to save battery
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } else {
        // Page visible: immediately update and schedule next check
        setTick((prev) => prev + 1);
        scheduleNextCheck();
      }
    };

    // Start scheduling
    scheduleNextCheck();

    // Listen for page visibility changes
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [items, getSchedule, timezone, enablePolling]);

  const activeItems = useMemo(() => {
    void tick;

    return items.filter((item) => {
      if (!item) {
        return false;
      }

      const schedule = getSchedule(item);

      if (!schedule) {
        return true;
      }

      return isScheduleActiveNow(schedule, timezone);
    });
  }, [items, getSchedule, timezone, tick]);

  return activeItems;
}
